import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useNow } from '@vueuse/core'
import { toNumber } from 'lodash'
import { mdiWrenchClock } from '@mdi/js'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import { isWithinInterval, minutesToMilliseconds, subMinutes } from 'date-fns'
import { useNotificationStore } from '@/modules/notification/stores/notification.store'
import { NotifyType } from '@/modules/notification/types/notification.enum'
import router from '@/router'

export const useMaintenanceStore = defineStore('maintenance', () => {
  const notificationStore = useNotificationStore()
  const now = useNow()
  const remindTimerId = ref<NodeJS.Timeout>()
  const timerId = ref<NodeJS.Timeout>()
  const activeFrom = ref<string>('')
  const activeTo = ref<string>('')
  const reminderFrom = ref<string>('')
  const reminderInterval = ref<number>(minutesToMilliseconds(1))
  const isMaintenance = ref<boolean>(false)

  const isMaintenanceActive = computed((): boolean =>
    isWithinInterval(now.value.toISOString(), {
      start: activeFrom.value,
      end: activeTo.value,
    })
  )

  const isReminderActive = computed((): boolean =>
    isWithinInterval(now.value.toISOString(), {
      start: reminderFrom.value,
      end: activeFrom.value,
    })
  )

  const addNotification = (): void => {
    notificationStore.notify({
      title: 'glossary.scheduledMaintenance',
      icon: mdiWrenchClock,
      from: activeFrom.value,
      to: activeTo.value,
      ago: new Date().toISOString(),
      type: NotifyType.System,
      isUnread: true,
    })
  }

  const clearTimer = (timerId?: NodeJS.Timeout): void => {
    if (timerId) {
      clearTimeout(timerId)
    }
  }

  const showReminder = (): void => {
    remindTimerId.value = setTimeout(() => {
      startReminder()
    }, minutesToMilliseconds(reminderInterval.value))
  }

  const startReminder = (): void => {
    clearTimer(remindTimerId.value)
    if (isReminderActive.value) {
      showReminder()
    } else {
      startMaintenanceCheck()
    }
  }

  const startReminderCheck = (): void => {
    if (isReminderActive.value) {
      addNotification()
      startReminder()
    } else {
      timerId.value = setTimeout(() => startMaintenanceCheck(), minutesToMilliseconds(1))
    }
  }

  const startMaintenanceCheck = async (): Promise<void> => {
    clearTimer(timerId.value)
    clearTimer(remindTimerId.value)
    isMaintenance.value = isMaintenanceActive.value
    if (isMaintenance.value) {
      await router.push({ name: ROUTE_NAMES.MAINTENANCE })
    }
    startReminderCheck()
  }

  const setMaintenanceSettings = (from?: string, to?: string, startFrom?: string, interval?: string): void => {
    if (from && to && startFrom && interval) {
      activeFrom.value = from
      activeTo.value = to
      reminderFrom.value = subMinutes(from, toNumber(startFrom)).toISOString()
      reminderInterval.value = toNumber(interval)
      startMaintenanceCheck()
    }
  }

  return {
    isMaintenance,
    activeFrom,
    activeTo,
    reminderFrom,
    reminderInterval,
    setMaintenanceSettings,
  }
})
